import React, { useState, useEffect, useRef, useCallback } from 'react'
import Offer from './Offer'
import { Offer as OfferType } from '../home/types'
import classNames from 'classnames'

const AUTO_ADVANCE_MS = 3000
const SWIPE_THRESHOLD = 50
const SLIDE_DURATION_MS = 350

const Offers = React.memo(function Offers({
  offers,
  venue_uuid,
  isVenue,
  pointerEventsDisabled,
}: {
  offers: OfferType[] | undefined
  venue_uuid: string
  isVenue?: boolean
  /** When true, carousel won't receive pointer events (e.g. when a modal is open) */
  pointerEventsDisabled?: boolean
}) {
  const list = offers ?? []
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isHolding, setIsHolding] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const touchStartX = useRef<number | null>(null)
  const transitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const skipTransitionRef = useRef(false)

  const goNext = useCallback(() => {
    if (list.length <= 1 || isTransitioning) return
    if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current)
    setIsTransitioning(true)
    setCurrentIndex((prev) => {
      const next = (prev + 1) % list.length
      skipTransitionRef.current = next === 0 && prev === list.length - 1
      return next
    })
    transitionTimerRef.current = setTimeout(() => {
      transitionTimerRef.current = null
      setIsTransitioning(false)
      skipTransitionRef.current = false
    }, SLIDE_DURATION_MS)
  }, [list.length, isTransitioning])

  const goPrev = useCallback(() => {
    if (list.length <= 1 || isTransitioning) return
    if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current)
    setIsTransitioning(true)
    setCurrentIndex((prev) => {
      const next = (prev - 1 + list.length) % list.length
      skipTransitionRef.current = next === list.length - 1 && prev === 0
      return next
    })
    transitionTimerRef.current = setTimeout(() => {
      transitionTimerRef.current = null
      setIsTransitioning(false)
      skipTransitionRef.current = false
    }, SLIDE_DURATION_MS)
  }, [list.length, isTransitioning])

  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current)
    }
  }, [])

  // Auto-advance every 3s, pause when holding or transitioning
  useEffect(() => {
    if (list.length <= 1 || isHolding || isTransitioning) return
    const id = setInterval(goNext, AUTO_ADVANCE_MS)
    return () => clearInterval(id)
  }, [list.length, isHolding, isTransitioning, goNext])

  // Clamp currentIndex when offers shrinks
  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, Math.max(0, list.length - 1)))
  }, [list.length])

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsHolding(true)
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    setIsHolding(false)
    const endX = e.changedTouches[0].clientX
    const startX = touchStartX.current
    touchStartX.current = null
    if (startX == null) return
    const deltaX = startX - endX
    if (deltaX > SWIPE_THRESHOLD) {
      e.preventDefault()
      goNext()
    } else if (deltaX < -SWIPE_THRESHOLD) {
      e.preventDefault()
      goPrev()
    }
  }

  const handleTouchCancel = () => {
    setIsHolding(false)
    touchStartX.current = null
  }

  const handleMouseDown = () => setIsHolding(true)
  const handleMouseUp = () => setIsHolding(false)
  const handleMouseLeave = () => setIsHolding(false)

  if (list.length === 0) return null

  const safeIndex = Math.min(currentIndex, Math.max(0, list.length - 1))

  return (
    <div
      id="offers-section"
      className={classNames('w-full xs:pb-0', { 'pb-36': isVenue, 'pointer-events-none': pointerEventsDisabled })}
    >
      {list.length > 1 ? (
        <section className="relative w-full mt-7 px-2 pb-4">
          <div
            className="relative flex w-full overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            {/* Left tap zone */}
            <button
              type="button"
              aria-label="Previous offer"
              className="absolute left-0 top-0 z-10 h-full w-12 cursor-pointer touch-none border-0 bg-transparent p-0"
              onClick={(e) => {
                e.stopPropagation()
                goPrev()
              }}
            />
            {/* Sliding carousel: all offers mounted, translate to show current */}
            <div className="relative flex min-h-[104px] min-w-0 flex-1 overflow-hidden">
              <div
                className="flex w-full flex-nowrap transition-transform ease-out"
                style={{
                  width: `${list.length * 100}%`,
                  transform: `translateX(-${safeIndex * (100 / list.length)}%)`,
                  transitionDuration: skipTransitionRef.current ? '0ms' : `${SLIDE_DURATION_MS}ms`,
                }}
              >
                {list.map((offer, index) => (
                  <div
                    key={offer.offer_id}
                    className="flex min-w-0 shrink-0 justify-center px-2"
                    style={{
                      flex: `0 0 ${100 / list.length}%`,
                      width: `${100 / list.length}%`,
                    }}
                  >
                    <Offer {...offer} venue_uuid={venue_uuid} single currentOffer={index + 1} totalOffers={list.length} />
                  </div>
                ))}
              </div>
            </div>
            {/* Right tap zone */}
            <button
              type="button"
              aria-label="Next offer"
              className="absolute right-0 top-0 z-10 h-full w-12 cursor-pointer touch-none border-0 bg-transparent p-0"
              onClick={(e) => {
                e.stopPropagation()
                goNext()
              }}
            />
          </div>
        </section>
      ) : (
        <section className="flex w-full mt-3 px-2 pb-4">
          <Offer key={list[0]?.offer_id} {...list[0]} venue_uuid={venue_uuid} single />
        </section>
      )}
    </div>
  )
})

export default Offers
